import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function formatTaxonomyLabel(value) {
  if (!value) return '';

  const special = {
    'afro': 'Afro',
    'hip-hop': 'Hip-Hop',
    'rnb-soul': 'R&B / Soul',
    'reggae-dancehall': 'Reggae / Dancehall',
    'soundtrack-cinematic': 'Soundtrack / Cinematic',
    'drum-and-bass': 'Drum & Bass',
    'liquid-dnb': 'Liquid DnB',
    'atmospheric-dnb': 'Atmospheric DnB',
    'minimal-dnb': 'Minimal DnB',
    'dancefloor-dnb': 'Dancefloor DnB',
    'tech-house': 'Tech House',
    'afro-house': 'Afro House',
    'melodic-house': 'Melodic House',
    'melodic-techno': 'Melodic Techno',
    'melodic-dubstep': 'Melodic Dubstep',
    'deep-house': 'Deep House',
    'deep-dubstep': 'Deep Dubstep',
    'uk-garage': 'UK Garage',
    '2-step': '2-Step',
    'acid-house': 'Acid House',
    'acid-techno': 'Acid Techno',
    'idm': 'IDM',
    'j-pop': 'J-Pop',
    'k-pop': 'K-Pop',
    'rnb': 'R&B',
    'neo-soul': 'Neo-Soul',
    'alt-pop': 'Alt-Pop',
    'alt-rock': 'Alt-Rock',
  };

  if (special[value]) return special[value];

  return value
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function EditSongDialog({
  open,
  onOpenChange,
  song,
  onSaved,
}) {
  const [form, setForm] = useState({
    title: '',
    artist: '',
    genre: '',
    subgenre: '',
    microgenre: '',
    source_url: '',
    lyrics: '',
  });
  const [taxonomy, setTaxonomy] = useState({
    genres: [],
    subgenres_by_genre: {},
    microgenres_by_genre: {},
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !song) return;

    setForm({
      title: song.title || '',
      artist: song.artist || '',
      genre: song.genre || '',
      subgenre: song.subgenre || '',
      microgenre: song.microgenre || '',
      source_url:
        song.source_url ||
        song.spotify_url ||
        song.youtube_url ||
        song.soundcloud_url ||
        '',
      lyrics: song.lyrics || '',
    });

    api
      .get('/labels/filters')
      .then(r => {
        setTaxonomy(r.data || {
          genres: [],
          subgenres_by_genre: {},
          microgenres_by_genre: {},
        });
      })
      .catch(() => {});
  }, [open, song]);

  const save = async () => {
    if (!song?.song_id) return;

    if (!form.title.trim()) {
      toast.error('Title required');
      return;
    }

    if (!form.artist.trim()) {
      toast.error('Artist required');
      return;
    }

    if (!form.genre) {
      toast.error('Choose a genre for this song');
      return;
    }

    setSaving(true);

    try {
      await api.patch(`/songs/${song.song_id}`, {
        title: form.title,
        artist: form.artist,
        genre: form.genre,
        subgenre: form.subgenre || null,
        microgenre: form.microgenre || null,
        source_url: form.source_url?.trim() || null,
        lyrics: form.lyrics || null,
      });

      toast.success('Song updated');
      onOpenChange?.(false);
      onSaved?.();
    } catch (e) {
      toast.error(
        e.response?.data?.detail || 'Could not update song'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Edit song</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            placeholder="Title"
            value={form.title}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                title: e.target.value,
              }))
            }
          />

          <Input
            placeholder="Artist"
            value={form.artist}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                artist: e.target.value,
              }))
            }
          />

          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              Genre
            </label>

            <Select
              value={form.genre || undefined}
              onValueChange={value =>
                setForm(prev => ({
                  ...prev,
                  genre: value,
                  subgenre: '',
                  microgenre: '',
                }))
              }
            >
              <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="Select genre" />
              </SelectTrigger>

              <SelectContent>
                {taxonomy.genres.map(g => (
                  <SelectItem key={g} value={g}>
                    {formatTaxonomyLabel(g)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              Subgenre <span className="text-zinc-700">(optional)</span>
            </label>

            <Select
              value={form.subgenre || undefined}
              onValueChange={value =>
                setForm(prev => ({
                  ...prev,
                  subgenre: value === '__none__' ? '' : value,
                  microgenre: '',
                }))
              }
              disabled={!form.genre}
            >
              <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800">
                <SelectValue
                  placeholder={
                    form.genre
                      ? 'Select subgenre'
                      : 'Select genre first'
                  }
                />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="__none__">
                  No subgenre
                </SelectItem>

                {(taxonomy.subgenres_by_genre?.[form.genre] || []).map(sg => (
                  <SelectItem key={sg} value={sg}>
                    {formatTaxonomyLabel(sg)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
              Microgenre <span className="text-zinc-700">(optional)</span>
            </label>

            <Select
              value={form.microgenre || undefined}
              onValueChange={value =>
                setForm(prev => ({
                  ...prev,
                  microgenre: value === '__none__' ? '' : value,
                }))
              }
              disabled={!form.subgenre}
            >
              <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800">
                <SelectValue
                  placeholder={
                    form.subgenre
                      ? 'Select microgenre'
                      : 'Select subgenre first'
                  }
                />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="__none__">
                  No microgenre
                </SelectItem>

                {(taxonomy.microgenres_by_genre?.[form.genre]?.[form.subgenre] || []).map(mg => (
                  <SelectItem key={mg} value={mg}>
                    {formatTaxonomyLabel(mg)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Input
            placeholder="Source Link"
            value={form.source_url || ''}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                source_url: e.target.value,
              }))
            }
          />

          <Textarea
            placeholder="Lyrics"
            value={form.lyrics || ''}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                lyrics: e.target.value,
              }))
            }
            className="min-h-[180px]"
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            className="border-zinc-800"
          >
            Cancel
          </Button>

          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-neon rounded-full"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
